"use client";
import React, { createContext, useCallback, useContext, useState } from "react";
import { getCurrencyFromCookie } from "@/lib/currency/cookieUtils";
import {
  getFxQuoteCookie,
  type FxQuoteCookiePayload,
} from "@/lib/currency/fxQuoteCookieUtils";

export interface ConvertedPriceData {
  price: number;
  currencyCode: string;
  /**
   * USD amount this conversion was computed for. Part of the cache key (see
   * `convertedPriceKey`) so a converted amount can never be reused for a
   * different USD price, and so multiple different USD prices in the same
   * currency (e.g. a discounted price and a full list price rendered on the
   * same page) each keep their own cache entry instead of overwriting one
   * another.
   */
  sourcePrice: number;
}

export function convertedPriceKey(currencyCode: string, sourcePrice: number) {
  return `${currencyCode}:${sourcePrice}`;
}

interface UserPreferencesContextType {
  selectedCurrency: string;
  setSelectedCurrency: React.Dispatch<React.SetStateAction<string>>;
  convertedPrices: Record<string, ConvertedPriceData>;
  setConvertedPrice: (data: ConvertedPriceData) => void;
  clearConvertedPrices: () => void;
  fxQuote: FxQuoteCookiePayload | null;
  setFxQuote: React.Dispatch<React.SetStateAction<FxQuoteCookiePayload | null>>;
}

const UserPreferencesContext = createContext<
  UserPreferencesContextType | undefined
>(undefined);

export function UserPreferencesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const currencyFromCookie = getCurrencyFromCookie();
  const [selectedCurrency, setSelectedCurrency] = useState(currencyFromCookie);
  const [convertedPrices, setConvertedPrices] = useState<
    Record<string, ConvertedPriceData>
  >({});
  const [fxQuote, setFxQuote] = useState<FxQuoteCookiePayload | null>(() =>
    getFxQuoteCookie(),
  );

  const setConvertedPrice = useCallback((data: ConvertedPriceData) => {
    setConvertedPrices((prev) => ({
      ...prev,
      [convertedPriceKey(data.currencyCode, data.sourcePrice)]: data,
    }));
  }, []);

  const clearConvertedPrices = useCallback(() => setConvertedPrices({}), []);

  return (
    <UserPreferencesContext.Provider
      value={{
        selectedCurrency,
        setSelectedCurrency,
        convertedPrices,
        setConvertedPrice,
        clearConvertedPrices,
        fxQuote,
        setFxQuote,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
}

export const useUserPreferencesContext = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useUserPreferencesContext must be used within a UserPreferencesProvider",
    );
  }
  return context;
};
